import { useAuthStore } from '../stores/authStore';

// All API calls go through the Cloudflare Worker API Gateway
// The gateway handles routing to the correct Cloud Run service.
// The default matches the other auth/feedback entry points in the codebase
// so the frontend works even without a VITE_API_URL env var set.
const API_BASE = import.meta.env.VITE_API_URL || 'https://api-gateway.dasa-c17.workers.dev';

/**
 * Build a full URL against the API gateway. Used by `apiFetch` internally
 * and exposed for callers (e.g. EventSource for SSE) that need the full
 * URL up-front rather than going through the typed fetch wrapper.
 */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Typed fetch wrapper that routes to correct backend service,
 * injects auth headers, and handles error status codes.
 */
export async function apiFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const token = useAuthStore.getState().token;

  const headers = new Headers(options?.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!headers.has('Content-Type') && options?.body) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      /* empty — body may not be JSON */
    }

    // Best-effort endpoints — fire-and-forget side-effects whose 401 must
    // never tear down the user's session. Their callers all use .catch(()=>{})
    // (Topbar handleOrgChange/handleModeChange, AuthGuard initSession). If the
    // backend's session store is briefly unavailable (e.g. Redis cold-start
    // after a Cloud Run redeploy), we want the request to fail silently — not
    // log the user out. AuthGuard's user-rehydration effect (added separately)
    // handles legitimate session expiry on the next protected-route mount.
    const isBestEffort =
      path.startsWith('/api/v2/auth/session/') ||
      path.startsWith('/api/v2/auth/refresh');

    if (res.status === 401 && !isBestEffort) {
      useAuthStore.getState().clearAuth();
      window.location.href = '/auth';
    }

    const msg =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as Record<string, unknown>).message)
        : res.statusText;

    throw new ApiError(res.status, msg, body);
  }

  // 204 No Content — nothing to parse
  if (res.status === 204) {
    return undefined as unknown as T;
  }

  return res.json() as Promise<T>;
}
